import {
  CacheUpdateEvent,
  CacheDeleteEvent,
} from '@/rec-cache/rec-cache-events'

import { ObjectProperty } from './rec-cache-types'
import { RebuildScheduler } from './rec-cache-rebuild-policy'

// A root type gets its own top-level group once it has at least this many
// roots; a root alone of its type stays at the top level as it is.
const MIN_TYPE_GROUP_SIZE = 2

/**
 * TreeSelection represents a tree view of the cache with lazy building and reactivity.
 * Maintains a hierarchical structure of TreeNode objects.
 */
class TreeSelection {
  constructor(selector, refreshFn, options = {}) {
    this.selector = selector
    this.refreshFn = refreshFn

    // Options
    this.superRoots = options.superRoots || null // Group by type
    this.sortFn = options.sortFn || this.defaultSort
    this.maxDepth = options.maxDepth || Infinity

    // Tree structure
    this._tree = [] // Array<TreeNode>
    this._flatMap = new Map() // id → TreeNode (for quick lookup)
    this._isDirty = true
    this._isBuilding = false
    this._isBuilt = false // Track if tree has been built at least once
    // Cost of the most recent rebuild, split by phase. The total is what
    // blocks the main thread, and what the rebuild interval is derived from.
    this._lastDurationMs = 0
    this._lastRootsMs = 0
    this._lastBuildMs = 0
    this._lastPublishMs = 0

    // Cache revision the current tree was built from; see the skip in the
    // scheduler's run below.
    this._lastRevision = -1

    // Set by the bridge so a view can show that a rebuild is under way.
    this.onRunningChange = null

    this._scheduler = new RebuildScheduler({
      run: () => {
        // Roots are a precondition of the rebuild and are not maintained
        // incrementally — recomputing them is a full scan of both edge maps
        // plus every id, so it belongs on the throttled path next to the
        // rebuild it serves, not on each change event.
        // Nothing has changed in the cache since the tree was last built, so
        // this rebuild would spend its eleven seconds reproducing the tree
        // already on screen. Three of those ran back to back on the Fabege
        // graph — identical inputs, identical 29,227-node output.
        const revision = this.selector.cache.revision
        if (this._isBuilt && revision === this._lastRevision) {
          console.log(
            `🌲 TreeSelection: cache unchanged at revision ${revision} — skipping rebuild`
          )
          this._isDirty = false
          return
        }

        const t0 = performance.now()
        this.selector.updateRoots()
        this._rebuild(performance.now() - t0)
        this._lastRevision = revision
      },
      isSyncing: () => !this.selector.cache.isSynced,
      lastDurationMs: () => this._lastDurationMs,
      onStateChange: (running) => {
        if (this.onRunningChange) this.onRunningChange(running)
      },
    })
  }

  /**
   * Register a viewer of this selection. Rebuilds only happen while at least
   * one viewer is registered — an unobserved selection stays dirty and
   * catches up the moment someone observes it.
   *
   * Returns an unsubscribe function; use it rather than tracking the pairing
   * by hand, since a missed release leaks a viewer and the selection then
   * rebuilds for nobody forever.
   */
  observe() {
    return this._scheduler.observe()
  }

  get isObserved() {
    return this._scheduler.isObserved
  }

  /**
   * Data changed. Rebuilds if observed, at a cadence the scheduler derives
   * from what the last rebuild cost. Unlike rebuild(), this may do nothing
   * now and rebuild later — or never, if nobody is looking.
   */
  requestRebuild() {
    this._isDirty = true
    this._scheduler.request()
  }

  get tree() {
    // Lazy build on access if dirty
    if (this._isDirty && !this._isBuilding) {
      console.log('🔍 TreeSelection.tree getter triggering lazy build')
      this._rebuild()
    }
    return this._tree
  }

  get isBuilt() {
    return this._isBuilt && !this._isDirty
  }

  /**
   * True once a tree has been produced, and stays true while later rebuilds
   * are pending — unlike isBuilt, which goes false again on every change.
   * A view distinguishing "no tree yet" from "tree present, refresh due"
   * wants this one; swapping the tree out for a placeholder mid-session
   * would throw away scroll position and expanded state.
   */
  get hasEverBuilt() {
    return this._isBuilt
  }

  get isBuilding() {
    return this._isBuilding
  }

  /**
   * True from the moment a scheduled rebuild is announced until it finishes.
   * Unlike isBuilding, which is only ever true inside the synchronous rebuild
   * and so can never be observed from a rendered frame, this is raised one
   * paint early precisely so a view can show it.
   */
  get isRunning() {
    return this._scheduler.isRunning
  }

  /**
   * Mark selection as dirty (needs rebuild).
   * Called by TreeSelector when data changes.
   */
  markDirty() {
    this.requestRebuild()
  }

  /**
   * Rebuild entire tree structure.
   * This is called lazily when tree is accessed and dirty.
   */
  _rebuild(rootsMs = 0) {
    const _t0 = performance.now()
    console.log('🌲 TreeSelection._rebuild() called', {
      roots: this.selector.roots.size,
      isolated: this.selector.isolated.size,
      upEdgeMap: this.selector.upEdgeMap.size,
      downEdgeMap: this.selector.downEdgeMap.size,
      individuals: this.selector.cache.individuals.length,
      isDirty: this._isDirty,
      isBuilt: this._isBuilt,
    })

    // Anomaly detection: many individuals but no edges → tree will be flat
    if (
      this.selector.cache.individuals.length > 10 &&
      this.selector.upEdgeMap.size === 0 &&
      this.selector.downEdgeMap.size === 0
    ) {
      console.warn(
        '🌲 ANOMALY: %d individuals but 0 tree edges — tree will be flat. ' +
          'This usually means edge extraction failed during hydration/sync.',
        this.selector.cache.individuals.length
      )
    }

    // Prevent recursive rebuilds
    if (this._isBuilding) return

    this._isBuilding = true
    this._tree = []
    this._flatMap.clear()

    // Diagnostics for a build that costs ~11s at 30,000 nodes — far more than
    // a walk of that graph should. Two candidate explanations, and these two
    // counters tell them apart in a single run.
    //
    // buildNode passes each child a *copy* of the visited set, so it tracks
    // the path rather than the graph: anything reachable by more than one
    // route has its whole subtree rebuilt once per route. _flatMap.set()
    // overwrites, so its size counts distinct nodes and hides that entirely.
    //
    // The other candidate is the label. Individual#label is an unmemoized
    // getter running nine linear scans of the property array, and every node
    // built reads it.
    this._buildNodeCalls = 0
    this._labelMs = 0
    this.selector.labelMisses = 0

    // Create O(1) lookup map for individuals to avoid repeated .find() calls
    const individualMap = new Map()
    this.selector.cache.individuals.forEach((ind) => {
      individualMap.set(ind.id.value, ind)
    })
    this._individualMap = individualMap

    // Same treatment for children. upEdgeMap is child -> parents, so asking it
    // for a node's children means scanning every entry to see which name that
    // node as a parent — O(edges) per node, and therefore O(nodes x edges) per
    // build. On the Fabege graph that was ~644 million Set.has() calls, which
    // is essentially the entire 12s: measured builds tracked the edge count
    // while the node count stayed put (12.2s at 22,365 edges, 15.1s at 23,002,
    // both producing 28,801 nodes).
    //
    // Inverting it once is O(edges). Built per rebuild and discarded after,
    // like _individualMap, so it cannot go stale as the maps change.
    const childrenByParent = new Map()
    const addChild = (parentId, childId) => {
      let children = childrenByParent.get(parentId)
      if (!children) childrenByParent.set(parentId, (children = new Set()))
      children.add(childId)
    }
    this.selector.upEdgeMap.forEach((parents, childId) => {
      parents.forEach((parentId) => addChild(parentId, childId))
    })
    this.selector.downEdgeMap.forEach((children, parentId) => {
      children.forEach((childId) => addChild(parentId, childId))
    })
    this._childrenByParent = childrenByParent

    // Build tree from each root
    this.selector.roots.forEach((rootId) => {
      const node = this.buildNode(rootId, 0)
      if (node) {
        this._tree.push(node)
      }
    })

    // Apply super-root grouping if configured (needs _individualMap for isolated nodes)
    if (this.superRoots) {
      this._tree = this.applySuperRoots(this._tree)
    }

    // Clear the maps to free memory
    this._individualMap = null
    this._childrenByParent = null

    // Sort tree. With super-roots the top level is already in its intended
    // order (see applySuperRoots), so only what is inside it gets sorted.
    if (this.superRoots) {
      this._tree.forEach((node) => this.sortTree(node.children))
    } else {
      this.sortTree(this._tree)
    }

    this._isDirty = false
    this._isBuilding = false
    this._isBuilt = true

    this._lastRootsMs = rootsMs
    this._lastBuildMs = performance.now() - _t0

    // Publishing is timed too. Whatever the consumer does with the tree
    // (the Vue bridge freezes it so Vue does not make ~45k nodes reactive)
    // blocks the same main thread as the build, so leaving it outside the
    // measurement understates what the user actually waits for — and
    // understates the interval the rebuild policy should be deriving from it.
    // The published tree may be frozen: never edit it in place.
    const _p0 = performance.now()
    if (this.refreshFn) {
      this.refreshFn(this._tree)
    }
    this._lastPublishMs = performance.now() - _p0

    this._lastDurationMs =
      this._lastRootsMs + this._lastBuildMs + this._lastPublishMs
    console.log(
      `🌲 TreeSelection rebuilt ${
        this._flatMap.size
      } nodes in ${this._lastDurationMs.toFixed(
        0
      )}ms — roots ${this._lastRootsMs.toFixed(
        0
      )}ms, build ${this._lastBuildMs.toFixed(
        0
      )}ms, publish ${this._lastPublishMs.toFixed(0)}ms`
    )
    console.log(
      `🌲 TreeSelection build detail — ${
        this._buildNodeCalls
      } buildNode calls for ${this._flatMap.size} nodes (${(
        this._buildNodeCalls / Math.max(1, this._flatMap.size)
      ).toFixed(1)}x), ${this._labelMs.toFixed(0)}ms reading label/type, ${
        this.selector.labelMisses
      } derived`
    )
  }

  /**
   * Recursively build a tree node and its children.
   * @param {string} id - Individual ID
   * @param {number} depth - Current depth in tree
   * @param {Set} visited - Set of visited IDs to prevent cycles
   * @returns {Object|null} TreeNode object or null
   */
  buildNode(id, depth, visited = new Set()) {
    this._buildNodeCalls++

    // Prevent circular references and max depth
    if (visited.has(id) || depth >= this.maxDepth) {
      return null
    }

    visited.add(id)

    // Get individual from cache - O(1) map lookup instead of O(n) find()
    const individual = this._individualMap.get(id)
    if (!individual) return null

    const _l0 = performance.now()
    const { name, type } = this.selector.nodeLabel(id, individual)
    this._labelMs += performance.now() - _l0

    const node = {
      id,
      name,
      type,
    }

    // Store in flat map for quick lookup
    this._flatMap.set(id, node)

    // Get children. Same result as selector.getChildren(id) — the union of
    // inverted up edges and down edges, deduplicated — read from the index
    // built once per rebuild rather than rescanned per node.
    const childIds = this._childrenByParent.get(id)
    if (childIds && childIds.size > 0) {
      node.children = [...childIds]
        .map((childId) => this.buildNode(childId, depth + 1, new Set(visited)))
        .filter((n) => n !== null)
    }

    return node
  }

  /**
   * Apply super-root grouping to organize top-level nodes by type: the
   * configured super-roots first, then a group per remaining root type.
   * @param {Array} nodes - Array of tree nodes
   * @returns {Array} Grouped tree nodes
   */
  applySuperRoots(nodes) {
    const grouped = Object.entries(this.superRoots)
      .map(([label, type]) => {
        const children = nodes.filter((n) => n.type === type)
        return {
          id: `group:${label.toLowerCase()}`,
          name: label,
          type: 'Group',
          children,
        }
      })
      .filter((node) => node.children && node.children.length > 0)

    const groupedIds = new Set(
      grouped.flatMap((root) => root.children.map((n) => n.id))
    )

    // Every other root type gets a group of its own once it has more than
    // one root. Loose at the top level they were unusable: on the Fabege
    // graph ~1,000 floating sensors and equipment sat there as ~1,100 rows,
    // which also made every render of the tree's top level that expensive.
    const byType = new Map()
    nodes
      .filter((n) => !groupedIds.has(n.id))
      .forEach((n) => {
        const type = n.type || 'Unknown'
        if (!byType.has(type)) byType.set(type, [])
        byType.get(type).push(n)
      })
    const typeGroups = []
    const remaining = []
    byType.forEach((members, type) => {
      if (members.length >= MIN_TYPE_GROUP_SIZE) {
        typeGroups.push({
          id: `group:type:${type.toLowerCase()}`,
          name: type,
          type: 'Group',
          children: members,
        })
      } else {
        remaining.push(...members)
      }
    })
    typeGroups.sort((a, b) => a.name.localeCompare(b.name))
    remaining.sort(this.sortFn)

    // Build isolated nodes group (nodes with no relationships)
    const isolatedNodes = this.buildIsolatedNodes()
    const isolatedGroup =
      isolatedNodes.length > 0
        ? [
            {
              id: 'group:isolated',
              name: 'Isolated',
              type: 'Group',
              children: isolatedNodes,
            },
          ]
        : []

    // Configured groups in their configured order, then the type groups
    // alphabetically, then the few roots alone of their type, then isolated.
    return [...grouped, ...typeGroups, ...remaining, ...isolatedGroup]
  }

  /**
   * Build tree nodes for isolated individuals (no relationships).
   * @returns {Array} Array of TreeNode objects
   */
  buildIsolatedNodes() {
    const nodes = []

    this.selector.isolated.forEach((id) => {
      const individual = this._individualMap?.get(id)
      if (individual) {
        const node = {
          id,
          name: individual.label.value,
          type: individual.type.short,
        }
        this._flatMap.set(id, node)
        nodes.push(node)
      }
    })

    return nodes
  }

  /**
   * Sort tree recursively.
   * @param {Array} nodes - Array of tree nodes to sort
   */
  sortTree(nodes) {
    if (!nodes || nodes.length === 0) return

    nodes.sort(this.sortFn)

    nodes.forEach((node) => {
      if (node.children) {
        this.sortTree(node.children)
      }
    })
  }

  /**
   * Default sort function: sort by type, then by name.
   */
  defaultSort(a, b) {
    if (a.type === b.type) {
      return a.name.localeCompare(b.name)
    }
    return a.type.localeCompare(b.type)
  }

  /**
   * Find a node by ID in the tree.
   * @param {string} id - Individual ID
   * @returns {Object|undefined} TreeNode or undefined
   */
  findNode(id) {
    return this._flatMap.get(id)
  }

  /**
   * Get path from root to node.
   * @param {string} id - Individual ID
   * @returns {Array<string>} Array of IDs from root to node
   */
  getPath(id) {
    const path = []
    let currentId = id

    while (currentId) {
      path.unshift(currentId)
      const parents = this.selector.getParents(currentId)
      currentId = parents[0] || null // Take first parent
    }

    return path
  }

  /**
   * Force immediate rebuild.
   */
  /** Rebuild now, observed or not — for explicit user actions. */
  rebuild() {
    this._isDirty = true
    this._scheduler.flush()
  }

  /**
   * Clear the tree selection.
   */
  clear() {
    this._tree = []
    this._flatMap.clear()
    this._isDirty = true
    this._isBuilt = false
    if (this.refreshFn) {
      this.refreshFn(this._tree)
    }
  }
}

/**
 * TreeSelector builds and maintains tree indices during the read cycle.
 * Passively accumulates edge data. Tree building is triggered by the app layer.
 */
class TreeSelector {
  constructor(dbs, upEdges = [], downEdges = []) {
    this.cache = dbs.cache
    this.edges = dbs.edges
    this.ids = dbs.ids

    // Tree edge configuration
    this.upEdges = upEdges // Array of relationship IRIs (child → parent)
    this.downEdges = downEdges // Array of relationship IRIs (parent → child)

    // Core indices
    this.upEdgeMap = new Map() // id → Set<parentId>
    this.downEdgeMap = new Map() // id → Set<childId>
    this.roots = new Set() // ids with no parents
    this.isolated = new Set() // ids with no relationships at all

    // State tracking
    this.isDirty = false
    this._states = [] // Selection instances

    // Tree labels, per id: { name, type }. See nodeLabel().
    this._labels = new Map()
    this.labelMisses = 0

    // Event listeners
    addEventListener(CacheUpdateEvent, this.update.bind(this))
    addEventListener(CacheDeleteEvent, this.onDelete.bind(this))
  }

  /**
   * Called when an individual is added/updated in the cache.
   * Passively accumulates edge data without triggering builds.
   */
  // Individual#label is expensive — nine linear scans of the property array
  // plus allocations — and rarely changes, yet every rebuild re-derived all of
  // them: ~350ms of a ~550ms build on the Fabege graph, repeated on every
  // rebuild even when the rebuild reproduced an identical tree. Cached per id
  // here; update() and onDelete() invalidate exactly the ids whose label can
  // have changed.
  //
  // Deliberately not memoized on the individual itself: views such as the
  // edit forms rely on Vue tracking the deep reads inside that getter to
  // re-render a label as it is edited, and a memo hit would skip them. The
  // tree only shows labels as of its last rebuild anyway.
  nodeLabel(id, individual) {
    let entry = this._labels.get(id)
    if (!entry) {
      entry = { name: individual.label.value, type: individual.type.short }
      this._labels.set(id, entry)
      this.labelMisses++
    }
    return entry
  }

  _invalidateLabel(id) {
    this._labels.delete(id)
    // A component's content feeds its parent's label — an Entry is labelled
    // from its postal-address component — and the parent receives no update
    // event when a component arrives or changes. RecCache records the parent
    // in `aggregates` before it announces the component, so it is known here.
    const parent = this.cache.aggregates?.get(id)
    if (parent) this._labels.delete(parent.value)
  }

  update({ detail: individual }) {
    const id = individual.id.value

    // Extract up edges (this individual → parent)
    const upTargets = individual.properties
      .filter((p) => p instanceof ObjectProperty)
      .filter((p) => this.upEdges.includes(p.name.value))
      .flatMap((p) => p.values.map((v) => v.value))

    // Extract down edges (this individual → children)
    const downTargets = individual.properties
      .filter((p) => p instanceof ObjectProperty)
      .filter((p) => this.downEdges.includes(p.name.value))
      .flatMap((p) => p.values.map((v) => v.value))

    // Update edge maps — clear stale entries when relationships are removed
    if (upTargets.length > 0) {
      this.upEdgeMap.set(id, new Set(upTargets))
    } else {
      this.upEdgeMap.delete(id)
    }

    if (downTargets.length > 0) {
      this.downEdgeMap.set(id, new Set(downTargets))
    } else {
      this.downEdgeMap.delete(id)
    }

    this._invalidateLabel(id)

    // Mark as dirty (tree needs rebuild when accessed)
    this.isDirty = true
  }

  /**
   * Called when an individual is deleted from the cache.
   * Removes all edge references to/from the deleted individual.
   */
  onDelete({ detail: id }) {
    this.upEdgeMap.delete(id)
    this.downEdgeMap.delete(id)
    this._invalidateLabel(id)

    // Also remove references to the deleted individual from other entries
    this.upEdgeMap.forEach((parents, childId) => {
      if (parents.has(id)) {
        parents.delete(id)
        if (parents.size === 0) {
          this.upEdgeMap.delete(childId)
        }
      }
    })
    this.downEdgeMap.forEach((children, parentId) => {
      if (children.has(id)) {
        children.delete(id)
        if (children.size === 0) {
          this.downEdgeMap.delete(parentId)
        }
      }
    })

    this.isDirty = true
  }

  /**
   * Rebuild edge maps from scratch using all individuals in the cache.
   * Self-healing: ensures tree edges are correct regardless of
   * incremental update history (IDB hydration, ETag-skipped syncs, etc.).
   */
  rebuildEdges() {
    const prevUp = this.upEdgeMap.size
    const prevDown = this.downEdgeMap.size

    this.upEdgeMap.clear()
    this.downEdgeMap.clear()

    this.cache.individuals.forEach((individual) => {
      this.update({ detail: individual })
    })

    console.log(
      `TreeSelector: rebuildEdges() — upEdgeMap: ${prevUp}→${this.upEdgeMap.size}, downEdgeMap: ${prevDown}→${this.downEdgeMap.size}`
    )
  }

  /**
   * Update the set of root nodes.
   * Roots are individuals with no up edges AND not targets of down edges.
   * Components are excluded from roots.
   *
   * IMPORTANT: Only individuals that appear in edges are considered.
   * Individuals with no relationships at all are excluded from the tree.
   */
  updateRoots() {
    this.roots.clear()

    // Collect all targets of down edges
    const downTargets = new Set()
    this.downEdgeMap.forEach((targets) => {
      targets.forEach((target) => downTargets.add(target))
    })

    // Collect all individuals that appear in ANY edge (up or down)
    const individualsWithEdges = new Set()

    // Add all individuals that have up edges (sources)
    this.upEdgeMap.forEach((parents, childId) => {
      individualsWithEdges.add(childId)
      parents.forEach((parentId) => individualsWithEdges.add(parentId))
    })

    // Add all individuals that have down edges (sources and targets)
    this.downEdgeMap.forEach((children, parentId) => {
      individualsWithEdges.add(parentId)
      children.forEach((childId) => individualsWithEdges.add(childId))
    })

    // Find roots: ONLY from individuals with edges
    // Roots are those that have no up edges AND are not targets of down edges
    individualsWithEdges.forEach((id) => {
      const hasUpEdges = this.upEdgeMap.has(id)
      const isDownTarget = downTargets.has(id)
      const isComponent = this.cache.isComponent({ value: id })

      if (!hasUpEdges && !isDownTarget && !isComponent) {
        this.roots.add(id)
      }
    })

    // Find isolated nodes: individuals with no relationships at all
    // These are nodes that exist in the cache but don't appear in any edge
    this.isolated.clear()
    this.ids.forEach((id) => {
      if (!individualsWithEdges.has(id)) {
        const isComponent = this.cache.isComponent({ value: id })
        if (!isComponent) {
          this.isolated.add(id)
        }
      }
    })
  }

  /**
   * Get all children of a node (both from up edges and down edges).
   * @param {string} id - Individual ID
   * @returns {Array<string>} Array of child IDs
   */
  /**
   * Children of a node: the union of up edges pointing at it and down edges
   * from it.
   *
   * This scans the whole upEdgeMap, so it is O(edges) per call — fine for the
   * one-off lookups it serves, but not for walking a graph. TreeSelection
   * builds an inverted index once per rebuild instead; see _rebuild().
   */
  getChildren(id) {
    // Up children: individuals that point to this one via up edge
    const upChildren = []
    this.upEdgeMap.forEach((parents, childId) => {
      if (parents.has(id)) {
        upChildren.push(childId)
      }
    })

    // Down children: individuals this one points to via down edge
    const downChildren = this.downEdgeMap.has(id)
      ? [...this.downEdgeMap.get(id)]
      : []

    return [...new Set([...upChildren, ...downChildren])]
  }

  /**
   * Get all parents of a node.
   * @param {string} id - Individual ID
   * @returns {Array<string>} Array of parent IDs
   */
  getParents(id) {
    // Up parents: individuals this one points to via up edge
    const upParents = this.upEdgeMap.has(id) ? [...this.upEdgeMap.get(id)] : []

    // Down parents: individuals that point to this one via down edge
    const downParents = []
    this.downEdgeMap.forEach((children, parentId) => {
      if (children.has(id)) {
        downParents.push(parentId)
      }
    })

    return [...new Set([...upParents, ...downParents])]
  }

  /**
   * Create a new tree selection.
   * @param {Function} refreshFn - Callback when tree changes
   * @param {Object} options - Configuration options
   * @returns {TreeSelection} New selection instance
   */
  createSelection(refreshFn = null, options = {}) {
    const selection = new TreeSelection(this, refreshFn, options)
    this._states.push(selection)
    return selection
  }

  /**
   * Clear all tree data.
   */
  clear() {
    this._labels.clear()
    this.upEdgeMap.clear()
    this.downEdgeMap.clear()
    this.roots.clear()
    this.isolated.clear()
    this.isDirty = false
    this._states.forEach((s) => s.clear())
  }
}

export { TreeSelector }
